import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Clock,
  MapPin,
  Star,
  ChevronLeft,
  ChevronRight,
  Plane,
  ArrowRight,
  User,
  Users,
  Shield,
  Phone,
  X,
} from "lucide-react";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";
import PackageBookingModal from "../../components/Packages/PackageBookingModal";
import service, { getImageUrl } from "../../Service/Service";
import "./PackageDetailPage.css";
import PriceDisplay from "../../components/PriceDisplay";

const PACKAGE_IMAGE_KEYS = [
  "makkah_img1",
  "makkah_img2",
  "makkah_img3",
  "makkah_img4",
  "madina_img1",
  "madina_img2",
  "madina_img3",
  "madina_img4",
];

const PHONE = "03338820922";

function getPackageImages(pkg) {
  return PACKAGE_IMAGE_KEYS.map((key) => pkg[key])
    .filter(Boolean)
    .map((raw) => getImageUrl(raw));
}

async function fetchPackageByIdentifier(identifier) {
  if (!identifier) return null;

  try {
    const response = await service.getpackagebyidUmrah({ uid: identifier });
    if (response.data?.[0]) return response.data[0];
  } catch {
    // fall through to slug lookup
  }

  try {
    const all = await service.getAllpackageUmrah();
    const list = all.data || [];
    return list.find(
      (item) =>
        String(item.seo_slug) === String(identifier) || String(item.uid) === String(identifier)
    );
  } catch {
    return null;
  }
}

function IncludedItem({ icon, label }) {
  return (
    <div className="pkg-detail-included-item">
      <div className="pkg-detail-included-icon">{icon}</div>
      <span>{label}</span>
    </div>
  );
}

function BookingCard({ pkg, adults, setAdults, totalNights, totalPrice, onBook }) {
  const starRating = [3, 4, 5].includes(Number(pkg.ratiing)) ? Number(pkg.ratiing) : null;
  const badgeLabel = starRating ? `${starRating} Star` : pkg.category_name;

  return (
    <div className="pkg-detail-booking-card">
      <div className="pkg-detail-booking-card-header">
        <p className="pkg-detail-booking-label">Package Price</p>
        <div className="pkg-detail-booking-price-row">
          <p className="pkg-detail-booking-price"><PriceDisplay amount={Number(pkg.price || 0)} /></p>
          <p className="pkg-detail-booking-per">per person</p>
        </div>
        {totalNights > 0 && (
          <div className="pkg-detail-booking-nights">
            <Clock className="pkg-detail-booking-nights-icon" />
            <span>{totalNights} nights included</span>
          </div>
        )}
        {badgeLabel && <span className="pkg-detail-booking-badge">{badgeLabel}</span>}
      </div>

      <div className="pkg-detail-booking-body">
        <p className="pkg-detail-booking-adults-label">Adults</p>
        <div className="pkg-detail-booking-counter">
          <button type="button" onClick={() => setAdults(Math.max(1, adults - 1))}>−</button>
          <span>{adults}</span>
          <button type="button" onClick={() => setAdults(Math.min(20, adults + 1))}>+</button>
        </div>

        <div className="pkg-detail-booking-total">
          <div>
            <p className="pkg-detail-booking-total-label">Total estimate</p>
            <p className="pkg-detail-booking-total-meta">
              {adults} adult{adults !== 1 ? "s" : ""}
            </p>
          </div>
          <p className="pkg-detail-booking-total-price"><PriceDisplay amount={totalPrice} /></p>
        </div>

        <button type="button" className="pkg-detail-booking-submit" onClick={onBook}>
          Request Booking
        </button>
        <p className="pkg-detail-booking-note">No payment required · Agent will call you</p>

        <div className="pkg-detail-booking-trust">
          {["✓ Free enquiry", "✓ No card", "✓ Fast reply"].map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>

        <a href={`tel:${PHONE}`} className="pkg-detail-booking-phone">
          <Phone className="pkg-detail-booking-phone-icon" />
          0333 8820922
        </a>
        <div className="pkg-detail-booking-contact-link">
          <Link to="/contact">Questions? Contact us</Link>
        </div>
      </div>
    </div>
  );
}

function MobileHero({ images, pkg, onOpen }) {
  const [index, setIndex] = useState(0);

  const prev = (event) => {
    event.stopPropagation();
    setIndex((current) => (current - 1 + images.length) % images.length);
  };

  const next = (event) => {
    event.stopPropagation();
    setIndex((current) => (current + 1) % images.length);
  };

  return (
    <div className="pkg-detail-mobile-hero" onClick={() => onOpen(index)} role="presentation">
      {images[index] ? (
        <img src={images[index]} alt={pkg.name} className="pkg-detail-mobile-hero-image" />
      ) : (
        <div className="pkg-detail-mobile-hero-fallback">
          <MapPin className="pkg-detail-mobile-hero-fallback-icon" />
        </div>
      )}
      <div className="pkg-detail-mobile-hero-overlay" />
      {images.length > 1 && (
        <>
          <button type="button" onClick={prev} className="pkg-detail-mobile-nav pkg-detail-mobile-nav--prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button type="button" onClick={next} className="pkg-detail-mobile-nav pkg-detail-mobile-nav--next">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="pkg-detail-mobile-dots">
            {images.map((_, dotIndex) => (
              <span
                key={dotIndex}
                className={`pkg-detail-mobile-dot ${dotIndex === index ? "pkg-detail-mobile-dot--active" : ""}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Lightbox({ images, index, onChange, onClose }) {
  useEffect(() => {
    const handler = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onChange((index - 1 + images.length) % images.length);
      if (event.key === "ArrowRight") onChange((index + 1) % images.length);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [index, images.length, onChange, onClose]);

  return (
    <div className="pkg-detail-lightbox">
      <div className="pkg-detail-lightbox-top">
        <p>
          {index + 1} / {images.length}
        </p>
        <button type="button" onClick={onClose} aria-label="Close gallery">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="pkg-detail-lightbox-main">
        <img src={images[index]} alt="" />
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="pkg-detail-lightbox-nav pkg-detail-lightbox-nav--prev"
              onClick={() => onChange((index - 1 + images.length) % images.length)}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              className="pkg-detail-lightbox-nav pkg-detail-lightbox-nav--next"
              onClick={() => onChange((index + 1) % images.length)}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="pkg-detail-lightbox-thumbs">
          {images.map((image, thumbIndex) => (
            <button
              key={image}
              type="button"
              className={`pkg-detail-lightbox-thumb ${thumbIndex === index ? "pkg-detail-lightbox-thumb--active" : ""}`}
              onClick={() => onChange(thumbIndex)}
            >
              <img src={image} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="pkg-detail-page">
      <Navbar bg="blue" />
      <div className="pkg-detail-skeleton-hero" />
      <div className="pkg-detail-skeleton-bar" />
      <div className="pkg-detail-content">
        <div className="pkg-detail-grid">
          <div className="pkg-detail-main">
            <div className="pkg-detail-skeleton-block pkg-detail-skeleton-block--tall" />
            <div className="pkg-detail-skeleton-block" />
          </div>
          <div className="pkg-detail-sidebar">
            <div className="pkg-detail-skeleton-block pkg-detail-skeleton-block--sidebar" />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const PackageDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const identifier = id || searchParams.get("package");

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [adults, setAdults] = useState(2);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (!identifier) {
      setNotFound(true);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setNotFound(false);

    fetchPackageByIdentifier(identifier)
      .then((data) => {
        if (!active) return;
        if (!data) setNotFound(true);
        else setPkg(data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [identifier]);

  if (loading) return <DetailSkeleton />;

  if (notFound || !pkg) {
    return (
      <div className="pkg-detail-page">
        <Navbar bg="blue" />
        <div className="pkg-detail-not-found">
          <p>Package not found.</p>
          <Link to="/packages">Back to packages</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const allImages = getPackageImages(pkg);
  const totalNights = (pkg.makkah_nights || 0) + (pkg.madina_nights || 0);
  const totalPrice = (pkg.price || 0) * adults;
  const starRating = [3, 4, 5].includes(Number(pkg.ratiing)) ? Number(pkg.ratiing) : null;
  const badgeLabel = starRating ? `${starRating} Star` : pkg.category_name;

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="pkg-detail-page">
      <Navbar bg="blue" />

      <div className="pkg-detail-hero-wrap">
        <div className="pkg-detail-gallery">
          <button type="button" className="pkg-detail-gallery-main" onClick={() => openLightbox(0)}>
            {allImages[0] ? (
              <img src={allImages[0]} alt={pkg.name} className="pkg-detail-gallery-image" />
            ) : (
              <div className="pkg-detail-gallery-fallback">
                <MapPin className="pkg-detail-gallery-fallback-icon" />
              </div>
            )}
          </button>

          {[1, 2, 3, 4].map((slot) => (
            <button
              key={slot}
              type="button"
              className="pkg-detail-gallery-thumb-btn"
              onClick={() => openLightbox(slot)}
            >
              {allImages[slot] ? (
                <img src={allImages[slot]} alt="" className="pkg-detail-gallery-image" />
              ) : (
                <div className="pkg-detail-gallery-fallback pkg-detail-gallery-fallback--small">
                  <MapPin className="pkg-detail-gallery-fallback-icon" />
                </div>
              )}
              {slot === 4 && allImages.length > 5 && (
                <div className="pkg-detail-gallery-more">+{allImages.length - 5} photos</div>
              )}
            </button>
          ))}
        </div>

        <MobileHero images={allImages} pkg={pkg} onOpen={openLightbox} />

        <div className="pkg-detail-hero-overlay">
          <div className="pkg-detail-hero-overlay-inner">
            <div className="pkg-detail-breadcrumbs">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/packages">Packages</Link>
              <span>/</span>
              <span>{pkg.name}</span>
            </div>
            {badgeLabel && <span className="pkg-detail-hero-badge">{badgeLabel}</span>}
            <h1 className="pkg-detail-hero-title">{pkg.name}</h1>
            {starRating > 0 && (
              <div className="pkg-detail-hero-stars">
                {Array.from({ length: 5 }).map((_, starIndex) => (
                  <Star
                    key={starIndex}
                    className={
                      starIndex < starRating
                        ? "pkg-detail-star pkg-detail-star--filled"
                        : "pkg-detail-star"
                    }
                  />
                ))}
                <span>{starRating} Star</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="pkg-detail-meta-bar">
        <div className="pkg-detail-meta-inner">
          {totalNights > 0 && (
            <>
              <div className="pkg-detail-meta-item">
                <Clock className="pkg-detail-meta-icon" />
                {totalNights} nights
              </div>
              <div className="pkg-detail-meta-divider" />
            </>
          )}
          {(pkg.dep_name || pkg.arrival_name) && (
            <>
              <div className="pkg-detail-meta-item">
                <Plane className="pkg-detail-meta-icon" />
                <span>{pkg.dep_name || pkg.dep_iatacode}</span>
                <ArrowRight className="pkg-detail-meta-arrow" />
                <span>{pkg.arrival_name || pkg.arr_iatacode}</span>
              </div>
              <div className="pkg-detail-meta-divider" />
            </>
          )}
          {badgeLabel && (
            <>
              <span className="pkg-detail-meta-chip">{badgeLabel}</span>
              <div className="pkg-detail-meta-divider" />
            </>
          )}
          <div className="pkg-detail-meta-item">
            <Shield className="pkg-detail-meta-icon pkg-detail-meta-icon--green" />
            Free enquiry
          </div>
        </div>
      </div>

      <div className="pkg-detail-content">
        <div className="pkg-detail-grid">
          <div className="pkg-detail-main">
            <div className="pkg-detail-mobile-heading">
              {badgeLabel && <span className="pkg-detail-mobile-badge">{badgeLabel}</span>}
              <h1>{pkg.name}</h1>
              {starRating > 0 && (
                <div className="pkg-detail-mobile-stars">
                  {Array.from({ length: 5 }).map((_, starIndex) => (
                    <Star
                      key={starIndex}
                      className={
                        starIndex < starRating
                          ? "pkg-detail-star pkg-detail-star--filled"
                          : "pkg-detail-star"
                      }
                    />
                  ))}
                  <span>{starRating} / 5</span>
                </div>
              )}
            </div>

            {pkg.description && (
              <div className="pkg-detail-card">
                <h2>About this package</h2>
                <p className="pkg-detail-description">{pkg.description}</p>
              </div>
            )}

            <div className="pkg-detail-card">
              <h2>What&apos;s included</h2>
              <div className="pkg-detail-included-grid">
                {totalNights > 0 && (
                  <IncludedItem
                    icon={<Clock className="h-4 w-4" />}
                    label={`${totalNights} nights accommodation`}
                  />
                )}
                {(pkg.dep_iatacode || pkg.dep_name) && (
                  <IncludedItem
                    icon={<Plane className="h-4 w-4" />}
                    label={`Flights from ${pkg.dep_name || pkg.dep_iatacode}`}
                  />
                )}
                <IncludedItem icon={<Users className="h-4 w-4" />} label="Dedicated travel agent" />
                <IncludedItem icon={<Shield className="h-4 w-4" />} label="No payment to enquire" />
                <IncludedItem icon={<Phone className="h-4 w-4" />} label="Agent calls you within 2 hours" />
              </div>
            </div>

            <button type="button" className="pkg-detail-back-link" onClick={() => navigate("/packages")}>
              <ChevronLeft className="h-4 w-4" />
              Back to all packages
            </button>
          </div>

          <div className="pkg-detail-sidebar">
            <BookingCard
              pkg={pkg}
              adults={adults}
              setAdults={setAdults}
              totalNights={totalNights}
              totalPrice={totalPrice}
              onBook={() => setBookingOpen(true)}
            />
          </div>
        </div>
      </div>

      <div className="pkg-detail-mobile-bar">
        <div className="pkg-detail-mobile-bar-price">
          <p>from</p>
          <p>
            <PriceDisplay amount={Number(pkg.price || 0)} />
            <span>/person</span>
          </p>
        </div>
        <div className="pkg-detail-mobile-bar-counter">
          <button type="button" onClick={() => setAdults((count) => Math.max(1, count - 1))}>−</button>
          <div className="pkg-detail-mobile-bar-guests">
            <div>
              {Array.from({ length: Math.min(adults, 3) }).map((_, guestIndex) => (
                <User key={guestIndex} className="h-3.5 w-3.5" style={{ marginLeft: guestIndex > 0 ? -4 : 0 }} />
              ))}
            </div>
            <span>{adults}</span>
          </div>
          <button type="button" onClick={() => setAdults((count) => Math.min(9, count + 1))}>+</button>
        </div>
        <button type="button" className="pkg-detail-mobile-bar-book" onClick={() => setBookingOpen(true)}>
          Book
        </button>
      </div>

      {lightboxOpen && allImages.length > 0 && (
        <Lightbox
          images={allImages}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <PackageBookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        packageId={pkg.uid}
        adults={adults}
        price={pkg.price || 0}
        packageName={pkg.name}
      />

      <Footer />
    </div>
  );
};

export default PackageDetailPage;
